import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  ReportProblem as IncidentIcon,
  HealthAndSafety as HealthIcon,
  LocalHospital as MedicalIcon,
} from '@mui/icons-material';

const IncidentesHSE = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);

  const incidentesData = [
    {
      id: 1,
      fecha: '15/01/2026',
      tipo: 'Cuasi-Accidente',
      severidad: 'Media',
      locacion: 'Pozo XY-123',
      descripcion: 'Caída de objeto desde altura. No hubo lesionados.',
      accionesTomadas: 'Revisión de procedimientos de izaje. Capacitación adicional.',
      estado: 'Cerrado',
      diasPerdidos: 0,
    },
    {
      id: 2,
      fecha: '02/01/2026',
      tipo: 'Accidente Leve',
      severidad: 'Baja',
      locacion: 'Pozo AB-456',
      descripcion: 'Corte menor en mano durante manipulación de herramientas.',
      accionesTomadas: 'Primeros auxilios. Revisión de uso de guantes.',
      estado: 'Cerrado',
      diasPerdidos: 0,
    },
  ];

  const estadisticas = [
    { label: 'Días sin Accidentes', value: '45', color: theme.palette.success.main, icon: <HealthIcon /> },
    { label: 'Incidentes 2026', value: '2', color: theme.palette.warning.main, icon: <IncidentIcon /> },
    { label: 'Días Perdidos', value: '0', color: theme.palette.info.main, icon: <MedicalIcon /> },
    { label: 'Índice TRIR', value: '0.8', color: theme.palette.primary.main, icon: <WarningIcon /> },
  ];

  const getSeveridadColor = (severidad) => {
    switch (severidad) {
      case 'Alta': return 'error';
      case 'Media': return 'warning';
      case 'Baja': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Incidentes HSE
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Registro y seguimiento de incidentes de seguridad
          </Typography>
        </Box>
        <Button variant="contained" color="error" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Reportar Incidente
        </Button>
      </Box>

      {/* Estadísticas HSE */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {estadisticas.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card sx={{ bgcolor: index === 0 ? alpha(theme.palette.success.main, 0.05) : 'transparent' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(stat.color, 0.1), color: stat.color }}>
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight={700} color={stat.color}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Banner Sin Accidentes */}
      <Card sx={{ mb: 4, bgcolor: alpha(theme.palette.success.main, 0.1), border: `2px solid ${theme.palette.success.main}` }}>
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          <HealthIcon sx={{ fontSize: 48, color: theme.palette.success.main, mb: 1 }} />
          <Typography variant="h5" fontWeight={700} color="success.main">
            ¡45 días sin accidentes!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sigamos trabajando seguros
          </Typography>
        </CardContent>
      </Card>

      {/* Lista de Incidentes */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Registro de Incidentes 2026
      </Typography>
      
      {incidentesData.map((incidente) => (
        <Accordion key={incidente.id} sx={{ mb: 2, '&:before': { display: 'none' }, borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
              <IncidentIcon color={incidente.severidad === 'Alta' ? 'error' : 'warning'} />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" fontWeight={600}>{incidente.tipo}</Typography>
                  <Chip label={incidente.severidad} size="small" color={getSeveridadColor(incidente.severidad)} />
                  <Chip label={incidente.estado} size="small" color={incidente.estado === 'Cerrado' ? 'success' : 'warning'} variant="outlined" />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {incidente.fecha} • {incidente.locacion}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600}>Descripción</Typography>
                <Typography variant="body2" color="text.secondary">{incidente.descripcion}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600}>Acciones Tomadas</Typography>
                <Typography variant="body2" color="text.secondary">{incidente.accionesTomadas}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Chip label={`Días perdidos: ${incidente.diasPerdidos}`} size="small" variant="outlined" />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: alpha(theme.palette.error.main, 0.05) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            Reportar Incidente HSE
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Fecha del Incidente" type="date" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Hora" type="time" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Incidente</InputLabel>
                <Select label="Tipo de Incidente">
                  <MenuItem value="cuasi">Cuasi-Accidente</MenuItem>
                  <MenuItem value="leve">Accidente Leve</MenuItem>
                  <MenuItem value="moderado">Accidente Moderado</MenuItem>
                  <MenuItem value="grave">Accidente Grave</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Severidad</InputLabel>
                <Select label="Severidad">
                  <MenuItem value="baja">Baja</MenuItem>
                  <MenuItem value="media">Media</MenuItem>
                  <MenuItem value="alta">Alta</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Locación</InputLabel>
                <Select label="Locación">
                  <MenuItem value="1">Pozo XY-123</MenuItem>
                  <MenuItem value="2">Pozo AB-456</MenuItem>
                  <MenuItem value="3">Taller Central</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descripción del Incidente" multiline rows={3} placeholder="Describa detalladamente qué ocurrió..." />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Personal Involucrado" placeholder="Nombres de las personas involucradas" />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox color="error" />}
                label="¿Hubo lesiones?"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Acciones Inmediatas Tomadas" multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={() => setOpenDialog(false)}>
            Reportar Incidente
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IncidentesHSE;
